import type {
  ChildrenCategoryData,
  FormSchema,
  Supply,
} from '~/landing/app/(garance)/cagnup/schema';

import { Badge, Card, CardContent, Typography } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import {
  formatCurrency,
  formatPercent,
} from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';

import { calculateTotalBudgetForCategory } from '~/landing/app/(garance)/cagnup/calculations';
import { CategoryIcon } from '~/landing/app/(garance)/cagnup/components';

type CategoryCardProps = {
  category: ChildrenCategoryData;
};

export const CategoryCard = ({ category }: CategoryCardProps) => {
  const t = useTranslations('landing.cagnup');
  const form = useFormContext<FormSchema>();
  const family = form.watch('family');

  const { activeIconClass, color, icon, id, supplies } = category;
  const count = family[id] || 0;

  const totalBudgetForCurrentCategory = calculateTotalBudgetForCategory(
    category,
    count
  );

  const allStores = Array.from(
    new Set(supplies.flatMap(({ stores }) => stores))
  );

  return (
    <Card>
      <CardContent className="flex h-full flex-col gap-3 p-6">
        <header className="flex items-start gap-4">
          <CategoryIcon
            className={cn({ [activeIconClass]: count > 0 })}
            icon={icon}
          />
          <div className="space-y-1">
            <Typography variant="title-4">
              {t(`categories.${id}.label`)}
            </Typography>
            {count > 0 && (
              <Typography className="text-muted-foreground" variant="helper">
                {t('estimatedSavings.card.childrenCount', {
                  count,
                })}
              </Typography>
            )}
          </div>
        </header>
        <Card>
          <CardContent
            className={cn('space-y-1 px-6 py-3 text-center', {
              'bg-muted': totalBudgetForCurrentCategory === 0,
            })}
          >
            <Typography variant="title-5">
              {formatCurrency(totalBudgetForCurrentCategory, {
                digits: 2,
              })}
            </Typography>
            <Typography className="text-muted-foreground" variant="helper">
              {t('estimatedSavings.card.totalBudget')}
            </Typography>
          </CardContent>
        </Card>
        {count ? (
          <>
            {supplies.map((supply: Supply) => (
              <Card key={supply.label}>
                <CardContent className="space-y-3 px-3.5 py-3">
                  <div className="flex justify-between gap-4">
                    <div className="space-y-1">
                      <Typography variant="body">{supply.label}</Typography>
                      <Typography className="font-semibold" variant="body">
                        {`${formatCurrency(count * supply.budget)} x `}
                        <span
                          className={cn(
                            'inline-block rounded-sm bg-gray-200 px-1 font-semibold'
                          )}
                        >
                          {formatPercent(supply.cashback.percent, {
                            digits: 2,
                          })}
                        </span>
                      </Typography>
                    </div>
                    <Typography className="font-semibold" variant="body">
                      {`+${formatCurrency(
                        count * supply.cashback.percent * supply.budget,
                        {
                          digits: 2,
                        }
                      )}`}
                    </Typography>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {supply.stores.map((store) => (
                      <Badge key={store} className={color}>
                        {store}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : (
          <Card className="h-full self-stretch">
            <CardContent className="space-y-3 px-6 py-3">
              <Typography variant="body">
                {t('estimatedSavings.card.availablePartners')}
              </Typography>
              <div className="flex flex-wrap gap-3">
                {allStores.map((store) => (
                  <Badge key={store} className="bg-muted-foreground" size="lg">
                    {store}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};
